/**
 * The card chrome shared by the New exploration cards
 * (pages/colorExploration/setup/SetupCard.js) and the resource cards of the
 * Lasers, Substrates and Calibrations pages (ResourceCard.js): the rounded
 * white frame that lifts when expanded, the 34 px round badge, the value
 * pill and the indented details surface. One place, so the pages keep the
 * same look.
 */
export const MONO_FONT = 'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace';

export const INK = "#0f172a";
export const HAIRLINE = "rgba(15, 23, 42, 0.08)";

/** Badge tones: the New exploration page's states, plus a blue accent. */
export const BADGE_TONES = {
  complete: { bgcolor: "#e3f4e8", color: "#1b7f3b" },
  missing: { bgcolor: "#fff1dc", color: "#b45309" },
  neutral: { bgcolor: "#e9eef5", color: "#334155" },
  loading: { bgcolor: "#eef2f7", color: "#94a3b8" },
  accent: { bgcolor: "#e6f0fb", color: "#1e4fa3" },
};

/** Pill tones: neutral grey, the amber "required" look, and the status colours. */
export const PILL_TONES = {
  neutral: { bgcolor: "#f3f5f9", color: INK },
  missing: { bgcolor: "#fff8ec", color: "#92400e" },
  accent: { bgcolor: "#e6f0fb", color: "#1e4fa3" },
  success: { bgcolor: "#e3f4e8", color: "#1b7f3b" },
  warning: { bgcolor: "#fff1dc", color: "#b45309" },
  error: { bgcolor: "#fde8e8", color: "#b42318" },
};

/** The card frame; `open` lifts it with a blue border and a shadow. */
export const cardFrameSx = (open, { interactive = true } = {}) => ({
  width: "100%",
  borderRadius: 3,
  border: "1px solid",
  borderColor: open ? "rgba(76, 126, 255, 0.45)" : HAIRLINE,
  backgroundColor: "#ffffff",
  boxShadow: open ? "0 12px 32px rgba(15, 23, 42, 0.10)" : "0 1px 2px rgba(15, 23, 42, 0.04)",
  transition: "box-shadow 0.2s ease, border-color 0.2s ease",
  overflow: "hidden",
  "&:before": { display: "none" },
  ...(interactive
    ? {
        "&:hover": {
          borderColor: open ? "rgba(76, 126, 255, 0.6)" : "rgba(15, 23, 42, 0.18)",
        },
      }
    : {}),
});

/** The header row of a card (an AccordionSummary or a plain Box). */
export const cardSummarySx = {
  px: { xs: 2, md: 2.5 },
  minHeight: 72,
  "&.Mui-expanded": { minHeight: 72 },
  "& .MuiAccordionSummary-content": { my: 1.5, minWidth: 0, alignItems: "center", gap: 2 },
  "& .MuiAccordionSummary-content.Mui-expanded": { my: 1.5 },
  "& .MuiAccordionSummary-expandIconWrapper": { color: "text.secondary" },
};

/** The 34 px round badge left of the title. */
export const badgeSx = (tone) => ({
  width: 34,
  height: 34,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  fontWeight: 700,
  fontSize: "0.85rem",
  fontFamily: (theme) => theme.typography.fontFamily,
  transition: "background-color 0.2s ease, color 0.2s ease",
  ...(BADGE_TONES[tone] || BADGE_TONES.neutral),
});

/** A value pill: the one-line summary of a collapsed card or one fact of a row. */
export const valuePillSx = ({ tone = "neutral", mono = false, disabled = false } = {}) => ({
  display: "inline-block",
  px: 1.25,
  py: 0.5,
  borderRadius: 999,
  border: "1px solid rgba(15, 23, 42, 0.06)",
  bgcolor: (PILL_TONES[tone] || PILL_TONES.neutral).bgcolor,
  color: disabled ? "text.disabled" : (PILL_TONES[tone] || PILL_TONES.neutral).color,
  fontWeight: 600,
  fontSize: mono ? "0.76rem" : "0.8rem",
  fontFamily: mono ? MONO_FONT : (theme) => theme.typography.fontFamily,
  lineHeight: 1.5,
});

/** The details surface below the header, indented to the title. */
export const cardDetailsSx = {
  px: { xs: 2, md: 2.5 },
  pl: { md: "calc(20px + 34px + 16px)" },
  pt: 2.5,
  pb: 3,
  borderTop: "1px solid rgba(15, 23, 42, 0.06)",
  bgcolor: "#fcfcfd",
};

/** The sticky side panel beside a card list (ReviewPanel, ResourcePanel). */
export const sidePanelSx = {
  position: { md: "sticky" },
  top: { md: 16 },
  borderRadius: 3,
  border: `1px solid ${HAIRLINE}`,
  bgcolor: "#ffffff",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  overflow: "hidden",
};

/** The navy band at the top of a side panel: the side menu's gradient, so
 *  the panels read as the same chrome. */
export const sidePanelBandSx = {
  px: 2.5,
  pt: 2.25,
  pb: 2,
  background: "linear-gradient(180deg, #1a2230 0%, #202b3d 100%)",
  color: "#ffffff",
};

/** The overline of a group or panel section. */
export const overlineSx = {
  display: "block",
  letterSpacing: 1.2,
  fontWeight: 700,
  lineHeight: 1.6,
  color: "text.secondary",
};

/** A compact data table inside a card body: quiet header, monospace numbers. */
export const dataTableSx = {
  "& th": {
    fontWeight: 700,
    fontSize: "0.7rem",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "text.secondary",
    borderBottom: "1px solid rgba(15, 23, 42, 0.12)",
    whiteSpace: "nowrap",
  },
  "& td": {
    fontFamily: MONO_FONT,
    fontSize: "0.8rem",
    color: INK,
    borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
  },
  "& tr:last-of-type td": { borderBottom: "none" },
  "& td:first-of-type": { fontFamily: (theme) => theme.typography.fontFamily, fontWeight: 600 },
};
